import { NodeViewSettingsPanel } from '@/components/common/node-view-settings-panel';
import { CommonNodeViewWrapper, DEFAULT_CONTENT_CLASS } from '@/components/common/node-view-wrapper';
import type { JSONContent, ReactNodeViewProps } from '@tiptap/react';
import { NodeViewContent } from '@tiptap/react';

const ZYASIDE_TYPE_OPTIONS = [
    { value: 'elaboration', label: 'Elaboration' },
    { value: 'block', label: 'Block' },
    { value: 'bordered', label: 'Bordered' },
    { value: 'learning objective', label: 'Learning Objective' },
];

/**
 * True when the node has the same content as insertZyAside default: a single empty paragraph.
 * @param node.content: ProseMirror Fragment to check.
 * @returns Boolean indicating if the node content is empty.
 */
function isZyAsideContentEmpty(node: ReactNodeViewProps['node']): boolean {
    const { content } = node;
    if (content.childCount !== 1) return false;
    const first = content.firstChild;
    return first?.type.name === 'paragraph' && first.content.size === 0;
}

export function ZyAsideNodeView(props: ReactNodeViewProps) {
    const { node, updateAttributes, selected, decorations } = props;
    const caption = node.attrs.caption || '';
    const type = node.attrs.type || 'elaboration';

    const handleCaptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateAttributes({ caption: e.target.value || '' });
    };

    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newType = e.target.value;

        // If the node type is changing to 'learning objective' and the content is empty, insert the default template content
        if (newType === 'learning objective' && isZyAsideContentEmpty(node)) {
            const defaultContent: JSONContent = [
                { type: 'paragraph', content: [{ type: 'text', text: 'Learn to:' }] },
                {
                    type: 'bulletList',
                    content: [{ type: 'listItem', content: [{ type: 'paragraph' }] }],
                },
            ];
            const pos = props.getPos();
            if (typeof pos === 'number' && props.editor) {
                const from = pos + 1;
                const to = pos + node.nodeSize - 1;
                props.editor.chain().insertContentAt({ from, to }, defaultContent).run();
            }
        }
        updateAttributes({ type: newType });
    };

    const blockSpecificClasses = (type: string) => {
        switch (type) {
            case 'elaboration':
                return 'italic';
            case 'block':
                return 'text-white bg-sky-700 p-4 caret-white';
            case 'learning objective':
            case 'bordered':
                return 'border-y-2 border-gray-300 py-4';
            default:
                return '';
        }
    };

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            id="zyaside-node-view"
            label="zyAside"
            selected={selected}
            settingsPanel={
                <NodeViewSettingsPanel
                    fields={[
                        {
                            label: 'Caption',
                            type: 'text',
                            value: caption,
                            onChange: handleCaptionChange,
                            placeholder: 'Caption...',
                            searchAttr: 'caption',
                            disabled: type === 'learning objective',
                        },
                        {
                            label: 'Type',
                            type: 'select',
                            value: type,
                            onChange: handleTypeChange,
                            options: ZYASIDE_TYPE_OPTIONS,
                            searchAttr: 'type',
                        },
                    ]}
                />
            }
            contentClassName={`p-6 ${DEFAULT_CONTENT_CLASS} ${blockSpecificClasses(type)}`}
            fullscreenMode="inline"
            contentHeightClassName=""
            fullscreenContent={<NodeViewContent />}
            containerClassName="bg-white dark:bg-black"
        >
            <NodeViewContent />
        </CommonNodeViewWrapper>
    );
}
